import React, { useMemo } from 'react';

import { ExtractedEntity } from 'shiftNotes/_types';
import { Profile } from 'teamProfiles/_types';
import { deduplicateAndFilterOverlapping } from 'shiftNotes/helpers/nameHighlighting';
import * as Styled from 'shiftNotes/styles/NotesStack.style';

import ProfileTagButton from './ProfileTagButton';

interface NoteCardBodyProps {
  text: string;
  entities?: ExtractedEntity[];
  profiles?: Profile[];
  noteId?: string;
  disabled?: boolean;
  onSelectProfile?: (
    profile: Profile,
    entityExtractionId: string | undefined
  ) => void;
}

type Segment =
  | { type: 'text'; value: string }
  | {
      type: 'mention';
      key: string;
      label: string;
      profileId?: string;
      entityExtractionId: string;
      entityStartPosition: number;
    };

function buildSegments(text: string, entities: ExtractedEntity[]): Segment[] {
  if (!text) return [];
  if (!entities || entities.length === 0) {
    return [{ type: 'text', value: text }];
  }

  const personEntities = entities.filter(
    (e) => e.extraction_class_key.toLowerCase() === 'person'
  );
  const sorted = deduplicateAndFilterOverlapping(personEntities);

  const segments: Segment[] = [];
  let cursor = 0;

  sorted.forEach((entity) => {
    const start = Math.max(0, entity.start_position);
    const end = Math.max(start, entity.end_position);
    if (end > text.length || end <= start) return;

    if (start > cursor) {
      segments.push({ type: 'text', value: text.slice(cursor, start) });
    }

    const label = entity.profile?.full_name || text.slice(start, end);
    segments.push({
      type: 'mention',
      key: `${entity.id}-${start}`,
      label,
      profileId: entity.profile?.id,
      entityExtractionId: entity.id,
      entityStartPosition: start,
    });

    cursor = end;
  });

  if (cursor < text.length) {
    segments.push({ type: 'text', value: text.slice(cursor) });
  }

  return segments;
}

export default function NoteCardBody({
  text,
  entities,
  profiles = [],
  noteId,
  disabled = false,
  onSelectProfile,
}: NoteCardBodyProps): JSX.Element {
  const segments = useMemo(
    () => buildSegments(text, entities ?? []),
    [text, entities]
  );

  return (
    <Styled.Body as="div">
      {segments.map((segment, index) => {
        if (segment.type === 'text') {
          const textKey = `text-${index}`;
          return <React.Fragment key={textKey}>{segment.value}</React.Fragment>;
        }
        return (
          <ProfileTagButton
            key={segment.key}
            label={segment.label}
            profileId={segment.profileId}
            entityExtractionId={segment.entityExtractionId}
            entityStartPosition={segment.entityStartPosition}
            profiles={profiles}
            noteId={noteId}
            disabled={disabled}
            onSelectProfile={onSelectProfile}
          />
        );
      })}
    </Styled.Body>
  );
}
